import type { Competition } from '../../types/competition';
import type { UserStaff } from '../../types/user';

/**
 * The four ways to read a round page, one per job somebody does at it:
 *
 * - `judge` — the round's blocks, and from one of them everybody who climbs it,
 *   class by class (admin, editor, judge);
 * - `team` — one athlete at a time, their whole card, picked from a list: a
 *   squad's team manager, a parent at the mat with their children, and the desk
 *   (admin, editor), who may transcribe anybody's sheet that way;
 * - `athlete` — the viewer's own blocks, to mark their own card (a confirmed
 *   entry in one of the round's classes);
 * - `public` — the round's classes and their blocks, to read. Everybody has it.
 */
export type RoundView = 'judge' | 'team' | 'athlete' | 'public';

/** The order the default is taken in, widest job first. */
const PRIORITY: RoundView[] = ['judge', 'team', 'athlete', 'public'];

const RUNS_ROUNDS = ['admin', 'editor'];

export interface RoundViewsInput {
  staff: UserStaff[] | undefined;
  competition: Pick<Competition, 'slug' | 'managed_teams' | 'scorable_entries'> | null | undefined;
  /** The round's classes, when the answer is for one round. */
  categoryIds?: string[];
}

export function staffRolesHere(staff: UserStaff[] | undefined, slug: string | undefined): string[] {
  const here = staff?.find((row) => row.gym_slug === slug);
  return here && here.status === 'confirmed' ? here.staff_roles : [];
}

/** The viewer's own entry here, when they have one. */
export function ownEntry(competition: RoundViewsInput['competition']) {
  return competition?.scorable_entries?.find((entry) => entry.self) ?? null;
}

/** The views this viewer has on a round, in the order the default is chosen. */
export function availableRoundViews({ staff, competition, categoryIds }: RoundViewsInput): RoundView[] {
  const roles = staffRolesHere(staff, competition?.slug);
  const runsRounds = roles.some((role) => RUNS_ROUNDS.includes(role));
  const entries = competition?.scorable_entries ?? [];
  const inRound = (categoryId: string | null | undefined) =>
    !categoryIds || categoryIds.length === 0 || (!!categoryId && categoryIds.includes(categoryId));

  const views = new Set<RoundView>(['public']);
  if (runsRounds || roles.includes('judge')) views.add('judge');
  if (
    runsRounds ||
    (competition?.managed_teams?.length ?? 0) > 0 ||
    entries.some((entry) => !entry.self && inRound(entry.competition_category_id))
  ) {
    views.add('team');
  }
  const mine = ownEntry(competition);
  if (mine?.confirmed && inRound(mine.competition_category_id)) views.add('athlete');

  return PRIORITY.filter((view) => views.has(view));
}

/**
 * Whether there is a view to choose: two jobs or more here. Somebody with one
 * has nothing to pick — the round's card offers them no buttons, and the round
 * always opens on that job.
 */
export function hasViewChoice(views: RoundView[]): boolean {
  return views.filter((view) => view !== 'public').length >= 2;
}

// --- The view last chosen, per person and competition ------------------------

const VIEW_STORAGE_PREFIX = 'round-view:';

/** Who the choice belongs to: one browser may be signed in as several people. */
type Viewer = string | number | null | undefined;

const viewKey = (viewer: Viewer, slug: string) => `${VIEW_STORAGE_PREFIX}${viewer ?? 'anonymous'}:${slug}`;

/**
 * Kept in the browser, per person and competition: an admin who chose to work a
 * stage as a judge opens the next round as one too, and the one after that —
 * and the team manager who signs in on the same phone afterwards does not
 * inherit it.
 */
export function loadRoundView(viewer: Viewer, slug: string | undefined): RoundView | null {
  if (!slug) return null;
  try {
    const stored = localStorage.getItem(viewKey(viewer, slug));
    return PRIORITY.includes(stored as RoundView) ? (stored as RoundView) : null;
  } catch {
    return null;
  }
}

export function storeRoundView(viewer: Viewer, slug: string | undefined, view: RoundView) {
  if (!slug) return;
  try {
    localStorage.setItem(viewKey(viewer, slug), view);
  } catch {
    // Private mode or blocked storage: the page opens on the widest view.
  }
}

/**
 * The view a round opens on: the one the address asks for, then — for somebody
 * with a choice to make — the last one they chose at this competition, then the
 * widest job they have. Each is checked against what the viewer may have, so a
 * shared link to the judge's view opens the public one for somebody who is not
 * a judge.
 */
export function resolveRoundView(
  asked: string | null,
  available: RoundView[],
  slug: string | undefined,
  viewer: Viewer
): RoundView {
  const has = (view: string | null): view is RoundView => !!view && available.includes(view as RoundView);
  if (has(asked)) return asked;
  if (hasViewChoice(available)) {
    const remembered = loadRoundView(viewer, slug);
    if (has(remembered)) return remembered;
  }
  return available[0] ?? 'public';
}
